import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { alumnosProfesorService } from "../../../services/profesor";
import {
  AlumnosTable,
  CargaMasivaAlumnos,
  ProfesorLayout,
} from "../components";
import { AlumnoProfesor } from "./dto/alumnos.dto";

export const ProfesorAlumnosPage: React.FC = () => {
  const { user } = useAuth();
  const [alumnos, setAlumnos] = useState<AlumnoProfesor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroVigencia, setFiltroVigencia] = useState<
    "todos" | "vigente" | "no-vigente"
  >("vigente");
  const [filtroAnioEscolar, setFiltroAnioEscolar] = useState<string>("todos");
  const [filtroCurso, setFiltroCurso] = useState<string>("todos");
  const [aniosEscolares, setAniosEscolares] = useState<
    { idAnioEscolar: number; nombre: string; orden: number }[]
  >([]);
  const [cursos, setCursos] = useState<
    {
      idCurso: number;
      nombre: string;
      anio: number;
      anioEscolarId: number;
      AnioEscolar: {
        idAnioEscolar: number;
        nombre: string;
        orden: number;
      };
    }[]
  >([]);
  const [mostrarCargaMasiva, setMostrarCargaMasiva] = useState(false);

  useEffect(() => {
    if (user) {
      loadAlumnos();
      loadOpciones();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadAlumnos = async () => {
    try {
      setLoading(true);
      const data = await alumnosProfesorService.getAlumnos();
      setAlumnos(data);
    } catch (error) {
      console.error("Error cargando alumnos:", error);
      setAlumnos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOpciones = async () => {
    try {
      const [aniosData, cursosData] = await Promise.all([
        alumnosProfesorService.getAniosEscolares(),
        alumnosProfesorService.getCursosByColegio(),
      ]);
      setAniosEscolares(aniosData);
      setCursos(cursosData);
    } catch (error) {
      console.error("Error cargando opciones:", error);
    }
  };

  const cursosFiltradosPorAnio =
    filtroAnioEscolar !== "todos"
      ? cursos.filter((c) => c.AnioEscolar.nombre === filtroAnioEscolar)
      : cursos;

  const cursosUnicos = Array.from(
    new Set(cursosFiltradosPorAnio.map((c) => c.nombre))
  );

  const filteredAlumnos = alumnos.filter((alumno) => {
    const matchSearch =
      !searchTerm ||
      alumno.nombres.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumno.apellidos.toLowerCase().includes(searchTerm.toLowerCase()) ||
      alumno.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchVigencia =
      filtroVigencia === "todos" ||
      (filtroVigencia === "vigente" ? alumno.vigente : !alumno.vigente);

    const matchAnioEscolar =
      filtroAnioEscolar === "todos" ||
      alumno.cursos.some((curso) => curso.anioEscolar === filtroAnioEscolar);

    const matchCurso =
      filtroCurso === "todos" ||
      alumno.cursos.some((curso) => curso.nombre === filtroCurso);

    return matchSearch && matchVigencia && matchAnioEscolar && matchCurso;
  });

  const handleAnioEscolarChange = (anioEscolar: string) => {
    setFiltroAnioEscolar(anioEscolar);
    setFiltroCurso("todos");
  };

  return (
    <ProfesorLayout>
      <div className="w-full py-8">
        <div className="space-y-8 px-4 sm:px-6 lg:px-8">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 rounded-2xl shadow-2xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-16 w-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold">Mis Alumnos</h1>
                <p className="text-purple-100 mt-1">
                  Gestiona y monitorea el progreso de tus alumnos
                </p>
              </div>
            </div>
            <p className="text-purple-50 text-sm">
              Visualiza información detallada de tus estudiantes y sus
              asignaciones a cursos
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setMostrarCargaMasiva(true)}
              className="px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Carga
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-6">
            <div className="space-y-4">
              {/* Búsqueda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar Alumno
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por nombre o email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vigencia
                  </label>
                  <select
                    value={filtroVigencia}
                    onChange={(e) =>
                      setFiltroVigencia(
                        e.target.value as "todos" | "vigente" | "no-vigente"
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                  >
                    <option value="todos">Todos</option>
                    <option value="vigente">Vigente</option>
                    <option value="no-vigente">No vigente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Año Escolar
                  </label>
                  <select
                    value={filtroAnioEscolar}
                    onChange={(e) => handleAnioEscolarChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                  >
                    <option value="todos">Todos los años escolares</option>
                    {aniosEscolares.map((anio) => (
                      <option key={anio.idAnioEscolar} value={anio.nombre}>
                        {anio.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Curso
                  </label>
                  <select
                    value={filtroCurso}
                    onChange={(e) => setFiltroCurso(e.target.value)}
                    disabled={filtroAnioEscolar === "todos"}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    <option value="todos">
                      {filtroAnioEscolar === "todos"
                        ? "Seleccione un año escolar primero"
                        : "Todos los cursos"}
                    </option>
                    {cursosUnicos.map((curso) => (
                      <option key={curso} value={curso}>
                        {curso}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de alumnos */}
          <div className="w-full overflow-x-auto">
            <div className="bg-white rounded-xl shadow-lg border border-purple-100 inline-block min-w-full">
              <AlumnosTable
                alumnos={filteredAlumnos}
                loading={loading}
                reloadAlumnos={loadAlumnos}
              />
            </div>
          </div>

          {/* Info */}
          {!loading && (
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg border border-purple-200">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-sm font-medium">
                  {filteredAlumnos.length === 0 && alumnos.length === 0
                    ? "No hay alumnos registrados"
                    : filteredAlumnos.length === alumnos.length
                    ? `${alumnos.length} ${alumnos.length === 1 ? "alumno" : "alumnos"}`
                    : `Mostrando ${filteredAlumnos.length} de ${alumnos.length} ${alumnos.length === 1 ? "alumno" : "alumnos"}`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {mostrarCargaMasiva && (
        <CargaMasivaAlumnos
          onClose={() => setMostrarCargaMasiva(false)}
          onSuccess={() => {
            loadAlumnos();
            setMostrarCargaMasiva(false);
          }}
          aniosEscolares={aniosEscolares}
          cursos={cursos}
        />
      )}
    </ProfesorLayout>
  );
};
